import { useEffect, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { PARTY, STATE_NAMES } from "./shared";
import { RaceCard, StatusBadge } from "./RaceCard";

function HouseBlock({ house, districts }) {
  const [open, setOpen] = useState(false);
  if (!house) return null;
  const won = house.won || {};
  return (
    <div className="border-t border-black/10 pt-4" data-testid="midterms-panel-house">
      <div className="flex items-center justify-between">
        <div className="overline text-neutral-500">House · {house.total_seats} seat{house.total_seats > 1 ? "s" : ""}</div>
        <div className="flex gap-3 text-sm font-bold tabular-nums">
          <span style={{ color: PARTY.D.solid }}>D {won.D || 0}</span>
          <span style={{ color: PARTY.R.solid }}>R {won.R || 0}</span>
          {(won.I || 0) > 0 && <span style={{ color: PARTY.I.solid }}>O {won.I}</span>}
        </div>
      </div>
      {districts.length === 0 ? (
        <p className="mt-3 text-sm text-neutral-500 font-serif-ed italic">District results stream in once polls close.</p>
      ) : (
        <>
          <ul className="mt-2 divide-y divide-black/5">
            {(open ? districts : districts.slice(0, 5)).map((d) => (
              <li key={d.id} className="py-2 flex items-center gap-3 text-sm" data-testid={`midterms-district-${d.id}`}>
                <span className="w-20 shrink-0 font-mono-ed text-xs text-neutral-500">{d.label}</span>
                <span className="flex-1 min-w-0 truncate">{d.candidates?.[0]?.name || "—"}</span>
                <StatusBadge status={d.status} party={d.party} calledBy={d.status === "projected" ? "auto" : null} />
              </li>
            ))}
          </ul>
          {districts.length > 5 && (
            <button
              onClick={() => setOpen((o) => !o)}
              className="mt-2 text-[11px] uppercase tracking-[0.18em] font-bold hover:text-[var(--hinton-red)] transition-colors"
              data-testid="midterms-districts-toggle"
            >
              {open ? "Show fewer" : `All ${districts.length} districts`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

export function StatePanel({ code, onClose }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    const load = () => api.get(`/midterms/state/${code}`).then((r) => alive && setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [code]);

  return (
    <aside className="bg-white text-black p-5 md:p-6 shadow-2xl midterms-panel-in" data-testid="midterms-state-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="overline text-[var(--hinton-red)]">State results</div>
          <h3 className="font-display text-3xl font-black tracking-tight leading-none mt-1" data-testid="midterms-state-panel-name">
            {STATE_NAMES[code]}
          </h3>
        </div>
        <button onClick={onClose} className="p-2 -m-2 hover:text-[var(--hinton-red)] transition-colors" aria-label="Close" data-testid="midterms-state-panel-close">
          <X size={18} />
        </button>
      </div>
      {!data ? (
        <div className="py-12 flex justify-center"><Loader2 className="animate-spin" size={20} /></div>
      ) : (
        <div className="mt-5 space-y-5 max-h-[560px] overflow-y-auto pr-1">
          {data.senate.map((r) => <RaceCard key={r.id} title="Senate" race={r} testid="midterms-panel-senate" />)}
          <RaceCard title="Governor" race={data.governor} testid="midterms-panel-governor" />
          {data.senate.length === 0 && !data.governor && (
            <p className="text-sm text-neutral-500 font-serif-ed italic">No Senate or governor race in {data.state_name} this cycle.</p>
          )}
          <HouseBlock house={data.house} districts={data.districts || []} />
        </div>
      )}
    </aside>
  );
}
